import { useState, useEffect } from "react";
import axios from 'axios'
import { useParams, useNavigate } from 'react-router-dom';

import { Container, Form } from "react-bootstrap";
import { validateAndFocusForm } from '../../../utils/surveyValidationUtils';
import { useRealTimeValidation } from '../../../utils/realTimeValidationUtils';
import StandardEarlyPostOp from '../../../components/SelfAssessment/StandardEarlyPostOp/StandardEarlyPostOp';
import VAS from '../../../components/SelfAssessment/VAS/VAS';
import FormCompletion from '../../../components/SelfAssessment/FormCompletion/FormCompletion';
import CustomProgressBar from '../../../components/CustomProgressBar/CustomProgressBar';
import SurveyAlreadyCompleted from "../../../components/SelfAssessment/SurveyAlreadyCompleted/SurveyAlreadyCompleted";
import { BASE_URL } from "../../../utils/constants";

import './AfterXWeeks.scss'
import '../../../styles/survey-design-system.scss';
import '../../../styles/survey-validation.scss';

const AfterXWeeks = () => {
  const { eventId, customPatientId, week } = useParams();
  const treatmentSide = useParams()["treatmentSide"] === "left"? "Links" : "Rechts"
  const study = useParams()["study"].replace("-", " ")
  const navigate = useNavigate();
  useEffect(() => {
    if (!["2", "6"].includes(week) || !["elbow", "shoulder arthroscopy", "shoulder arthroplasty"].includes(study)) {
      navigate("/404")
    }
  });
  const numberOfSteps = week === "2"? 2 : 1;
  const [step, setStep] = useState(1);
  const [surveyCompleted, setSurveyCompleted] = useState(false)
  const [isCheckingCompletion, setIsCheckingCompletion] = useState(true)
  const [validated, setValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  
  const [formData, setFormData] = useState({
    vasScore: "",
    sepostop: {
      sepostopQuestion1: "",
      sepostopQuestion2: "",
      sepostopQuestion3: "",
    }
  })

  useEffect(() => {
    const checkSurveyCompletion = async () => {
      try {
        const stage = week === "2"? "two_weeks" : "six_weeks"
        const response = await axios.get(`${BASE_URL}/api/v1/self-assessment/check-survey-completion/${customPatientId}/${eventId}/${stage}`)
        setSurveyCompleted(response.data.completed)
      }
      catch (error) {
        console.log(error)
      }
      finally {
        setIsCheckingCompletion(false)
      }
    }

    checkSurveyCompletion()
  }, [customPatientId, eventId, week])


  const nextStep = () => {
    clearAllFieldValidations();
    setFieldErrors({});
    setTouchedFields({});
    const validationResult = validateAndFocusForm(formData, step, study, {
      clearPreviousErrors: true,
      showVisualFeedback: true,
      scrollToFirst: true
    });

    if (validationResult.isValid) {
      setValidated(false);
      setStep(step + 1);
    } else {
      setValidated(true);
      setValidationErrors(validationResult.invalidFields);
      setFieldErrors(validationResult.fieldErrors);
      const touched = Object.keys(validationResult.fieldErrors).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      setTouchedFields(touched);
      
    }
  };

  const prevStep = () => {
    clearAllFieldValidations();
    setValidationErrors([]);
    setStep(step - 1);
  };

  const { validateFieldRealTime, clearAllFieldValidations } = useRealTimeValidation(
    formData, step, study, setFieldErrors, setTouchedFields
  );

  const handleInputData = (type, input, value) => {
    let updatedFormData;
    switch(type) {
      case "sepostop":
        updatedFormData = { ...formData, sepostop: { ...formData.sepostop, [input]: value } };
        break;
      default:
        updatedFormData = { ...formData, [input]: value };
    }
    setFormData(updatedFormData);

    const fieldPath = (type === 'vas' || type === 'sane') ? input : `${type}.${input}`;
    validateFieldRealTime(fieldPath, value);

    if (validated) {
      setTimeout(() => {
        const validationResult = validateAndFocusForm(
          updatedFormData,
          step,
          study,
          {
            clearPreviousErrors: true,
            showVisualFeedback: true,
            scrollToFirst: false,
          }
        );
        setValidationErrors(validationResult.invalidFields);
        setFieldErrors(validationResult.fieldErrors);
      }, 0);
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    clearAllFieldValidations();
    const validationResult = validateAndFocusForm(formData, step, study, {
      clearPreviousErrors: true,
      showVisualFeedback: true,
      scrollToFirst: true
    });

    if (validationResult.isValid) {
      setIsSubmitting(true);
      try {
        await axios.post(`${BASE_URL}/api/v1/self-assessment/week/${week}`, {
          eventId: eventId,
          customPatientId: customPatientId,
          study: study,
          formAnswers: formData
        }, {headers:{"Content-Type" : "application/json"}})
        setStep(numberOfSteps + 1);
      }
      catch(error) {
        console.log(error.response.data)
      }
      finally {
        setIsSubmitting(false);
      }
    } else {
      setValidated(true);
      setValidationErrors(validationResult.invalidFields);
      setFieldErrors(validationResult.fieldErrors);
      const touched = Object.keys(validationResult.fieldErrors).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      setTouchedFields(touched);
      
    }
  };

  const renderFormContent = step => {
    switch (step){
      case 1:
        return <VAS handleFormData={handleInputData} values={formData} fieldErrors={fieldErrors} touchedFields={touchedFields} />
      case 2:
        if (week === "2") {
          return <StandardEarlyPostOp handleFormData={handleInputData} study={study} values={formData} fieldErrors={fieldErrors} touchedFields={touchedFields} />
        }
        else {
          return <FormCompletion />
        } 
      case 3:
        return <FormCompletion />
      default:
        return <div></div>
    }
  }

  return (
    <div className="modern-survey-container">
      <Form validated={validated} onSubmit={handleSubmit}>
        <div className="modern-survey-card">
          <div className="modern-survey-header">
            <div className="patient-identifier">
              <span className="patient-id-label">Patiënt:</span>
              <span className="patient-id-value">{customPatientId}</span>
            </div>
            <h1 className="survey-title">
              {study === "elbow"? "Elleboog" : "Schouder"} ({treatmentSide})
            </h1>
            <p className="survey-subtitle">Zelfevaluatie Na {week} Weken</p>
          </div>
          {isCheckingCompletion ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              Laden...
            </div>
          ) : !surveyCompleted? 
            (
              <>
              <div className="modern-progress-container">
                <CustomProgressBar className="form-progress-bar" currentStep={step} numberOfSteps={numberOfSteps} />
              </div>
              <div className="modern-survey-body">
                {validationErrors.length > 0 && (
                  <div className="validation-summary" role="alert">
                    <h3 className="summary-title">
                      Er zijn fouten gevonden
                    </h3>
                    <ul className="summary-list">
                      {validationErrors.map((error, index) => (
                        <li key={index}>
                          <button
                            type="button"
                            className="error-link"
                            onClick={() => {
                              const field = document.querySelector(error.selector);
                              if (field) {
                                field.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                field.focus();
                              }
                            }}
                          >
                            {error.message}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <Container className="form-content">
                  {
                    renderFormContent(step)
                  }
                </Container>
              </div>
                {step < (numberOfSteps + 1) ? (
                  <div className={`modern-survey-footer ${step === 1 ? 'single-button' : ''}`}>
                    {step !== 1 && (
                      <button
                        type="button"
                        className="modern-btn modern-btn-secondary"
                        onClick={prevStep}
                        disabled={isSubmitting}
                      >
                        Vorige Stap
                      </button>
                    )}
                    {step < numberOfSteps && (
                      <button
                        type="button"
                        className="modern-btn modern-btn-primary"
                        onClick={nextStep}
                        disabled={isSubmitting}
                      >
                        Volgende Stap
                      </button>
                    )}
                    {step === numberOfSteps && (
                      <button
                        type="submit"
                        className={`modern-btn modern-btn-primary ${isSubmitting ? 'modern-btn-loading' : ''}`}
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Bezig met indienen...' : 'Indienen'}
                      </button>
                    )}
                  </div>
                ) : null}
              </>
            ) :
            (
              <SurveyAlreadyCompleted />
            )
          }
        </div>
      </Form>
    </div>
  )
}

export default AfterXWeeks;